/**
 * Device detection utilities for responsive design
 */

export interface DeviceInfo {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  userAgent: string;
  screenWidth: number;
  screenHeight: number;
}

/**
 * Detect if the device is mobile based on user agent and screen size
 */
export const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;

  // Check user agent for mobile patterns
  const userAgent = navigator.userAgent.toLowerCase();
  const mobileKeywords = [
    'android', 'iphone', 'ipad', 'ipod', 'blackberry', 'iemobile',
    'opera mini', 'mobile', 'palm', 'smartphone', 'iphone', 'ipod',
    'windows phone', 'kindle'
  ];

  const isMobileUA = mobileKeywords.some(keyword => userAgent.includes(keyword));

  // Check screen size (max width 768px for mobile)
  const isMobileScreen = window.innerWidth <= 768;

  return isMobileUA || isMobileScreen;
};

/**
 * Detect if the device is tablet based on user agent and screen size
 */
export const isTabletDevice = (): boolean => {
  if (typeof window === 'undefined') return false;

  const userAgent = navigator.userAgent.toLowerCase();
  const isTabletUA = userAgent.includes('ipad') ||
    (userAgent.includes('android') && !userAgent.includes('mobile'));

  // Check screen size (768px to 1024px for tablets)
  const isTabletScreen = window.innerWidth > 768 && window.innerWidth <= 1024;

  return isTabletUA || isTabletScreen;
};

/**
 * Get comprehensive device information
 */
export const getDeviceInfo = (): DeviceInfo => {
  if (typeof window === 'undefined') {
    return {
      isMobile: false,
      isTablet: false,
      isDesktop: true,
      userAgent: '',
      screenWidth: 1920,
      screenHeight: 1080,
    };
  }

  const isMobile = isMobileDevice();
  const isTablet = isTabletDevice();
  const isDesktop = !isMobile && !isTablet;

  return {
    isMobile,
    isTablet,
    isDesktop,
    userAgent: navigator.userAgent,
    screenWidth: window.innerWidth,
    screenHeight: window.innerHeight,
  };
};

/**
 * Hook to track device changes on window resize
 */
export const useDeviceDetection = () => {
  const [deviceInfo, setDeviceInfo] = React.useState<DeviceInfo>(getDeviceInfo);

  React.useEffect(() => {
    const handleResize = () => {
      setDeviceInfo(getDeviceInfo());
    };

    window.addEventListener('resize', handleResize);

    // Initial detection
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return deviceInfo;
};

// Import React for the hook
import React from 'react';